import { ClockIcon } from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';

import { TabOption } from '@/components/chariot-ui';
import { technologyExportConfig } from '@/components/export/configs/technology.export.config';
import { UniversalExportModal } from '@/components/export/universal';
import { Container } from '@/components/legacy-ui';
import { NoteModal } from '@/components/modals/NoteModal';
import { Spinner } from '@/components/ui/spinner';
import { combineAssetNodeResults } from '@/hooks/useAssets';
import { useCreateAttribute } from '@/hooks/useAttribute';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useMy } from '@/hooks/useMy';
import { queryClient } from '@/queryclient';
import { AddTag, SimpleTags } from '@/sections/AddTag';
import { NotesTab } from '@/sections/NotesTab';
import { TechnologyActions } from '@/sections/technology/components/actions/TechnologyActions';
import { ConstantineModal } from '@/sections/technology/components/modals/ConstantineModal';
import { TechnologyRemoveTag } from '@/sections/technology/components/modals/RemoveTagModal';
import { TechnologyAssetsTab } from '@/sections/technology/components/tabs';
import { useTechnologyDrawerTab } from '@/sections/technology/hooks/drawer/useTechnologyDrawerTab';
import { useTechnologyActions } from '@/sections/technology/hooks/useTechnologyActions';
import { useAuth } from '@/state/auth';
import { useOsintContext } from '@/state/OsintContext';
import { Asset, Port } from '@/types';
import { cn } from '@/utils/classname';

import {
  TechnologyDrawerTabs,
  TechnologyEvidence,
  TechnologyHistory,
  TechnologyOsint,
} from './index';
import { TechnologyDrawerHeader } from './TechnologyDrawerHeader';

interface ImpactedAssetPort {
  asset: Asset;
  port: Port;
  visited: string;
}

const baseTechnologyTabOptions: TabOption[] = [
  {
    label: 'Assets Impacted',
    value: '',
  },
  // {
  //   label: 'Evidence',
  //   value: 'evidence',
  // },
  {
    label: 'History',
    value: 'history',
  },
  {
    label: 'Notes',
    value: 'notes',
  },
];

export function TechnologyDrawer({ drawerKey }: { drawerKey: string }) {
  // Local state for tab management - eliminates race conditions from URL-based state
  const [currentTab, setCurrentTab] = useTechnologyDrawerTab();
  const [constantineModalOpen, setConstantineModalOpen] = useState(false);
  const { isPraetorianUser } = useAuth();
  const { canWriteAssets } = useEntitlements();
  const { isOsint } = useOsintContext();

  const technologyTabOptions = useMemo(() => {
    if (isOsint) {
      return [{ label: 'Exploit', value: 'osint' }];
    }

    return [...baseTechnologyTabOptions, { label: 'Exploit', value: 'osint' }];
  }, [isOsint]);
  const { status: creatingAttribute } = useCreateAttribute();
  const { invalidate: invalidateTechnologyAttributes } = useMy(
    {
      query: {
        node: {
          labels: ['Attribute'],
          filters: [
            {
              field: 'source',
              operator: '=',
              value: drawerKey,
            },
          ],
        },
      },
      resource: 'attribute',
    },
    { enabled: Boolean(drawerKey) }
  );

  const { data: technologies, status: technologyStatus } = useMy(
    {
      resource: 'technology',
      query: {
        node: {
          labels: ['Technology'],
          filters: [
            {
              field: 'key',
              operator: '=',
              value: drawerKey,
            },
          ],
        },
      },
    },
    { enabled: Boolean(drawerKey) }
  );

  const technology = technologies?.[0];

  // Initialize technology actions hook with handlers
  const { modalState, modalHandlers, handlers } = useTechnologyActions({
    selectedTechnologies: technology ? [technology] : [],
    selectedRowsCount: technology ? 1 : 0,
    invalidateTechnologies: () => {
      queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'technology';
        },
        refetchType: 'active',
      });
      invalidateTechnologyAttributes();
    },
    setConfirmStatus: () => {},
  });

  /**
   * Assets Impacted Query
   *
   * FIXED: Race condition bug - removed `technologyStatus === 'success'` check
   *
   * The graph query already filters by drawerKey, so it's safe to start fetching
   * immediately when the drawer opens. The previous check created a race condition
   * where the query would be disabled during initial render and never auto-fetch.
   *
   * Pattern alignment: Matches working VulnerabilityDrawer implementation
   */
  const { data: impactedAssetsTree, status: impactedAssetsTreeStatus } = useMy(
    {
      resource: 'asset',
      tree: true,
      query: {
        node: {
          labels: ['Asset'],
          relationships: [
            {
              label: 'HAS_PORT',
              target: {
                labels: ['Port'],
                relationships: [
                  {
                    label: 'HAS_TECHNOLOGY',
                    target: {
                      labels: ['Technology'],
                      filters: [
                        {
                          field: 'key',
                          operator: '=',
                          value: drawerKey,
                        },
                      ],
                    },
                  },
                ],
              },
            },
          ],
        },
      },
    },
    { enabled: Boolean(drawerKey) }
  );

  const impactedAssetPorts = useMemo((): ImpactedAssetPort[] => {
    return impactedAssetsTree.flatMap(impactedAssetTree => {
      const asset = combineAssetNodeResults(impactedAssetTree)?.[0] as
        | Asset
        | undefined;
      const port = impactedAssetTree?.node?.relationships?.[0]?.target?.result
        ?.ports?.[0] as Port | undefined;

      if (!asset || !port) return [];

      return [
        {
          port,
          asset,
          visited: port.visited || '',
        },
      ];
    });
  }, [impactedAssetsTree]);

  // REMOVED: No longer filtering out items without attributes since we now support ports
  // const impactedAssetsWithAttribute = useMemo(
  //   () => impactedAssetAttributeUnion.filter(hasAttribute),
  //   [impactedAssetAttributeUnion]
  // );

  const userDefinedTags = useMemo(() => {
    return technology?.tags || [];
  }, [technology?.tags]);

  if (technologyStatus === 'pending') {
    return (
      <div className="flex size-full items-center justify-center p-6">
        <Spinner size="xl" />
      </div>
    );
  }

  if (!technology) {
    return (
      <div className="flex size-full min-w-[400px] items-center justify-center gap-4 p-6">
        <ClockIcon className="text-chariot-stroke size-6" />
        <span className="text-chariot-text-secondary">
          Technology is no longer detected in your attack surface
        </span>
      </div>
    );
  }

  return (
    <div className="group flex size-full flex-col gap-6 p-6 first:gap-0">
      <TechnologyDrawerHeader
        technology={technology}
        onShareClick={() => handlers.handleShare()}
      />

      <SimpleTags
        tags={userDefinedTags}
        onRemove={() => modalHandlers.openRemoveTagModal()}
        variant="compact"
      />

      <TechnologyActions
        selectedTechnologies={technology ? [technology] : []}
        selectedRowsCount={technology ? 1 : 0}
        modalHandlers={modalHandlers}
        actionHandlers={handlers}
        onClearSelection={() => {}}
        hideActions={false}
        isOsint={isOsint}
      />

      <div className="bg-chariot-stroke h-px w-full shrink-0"></div>

      <AddTag
        open={modalState.tagModal}
        onClose={modalHandlers.closeTagModal}
        description="Tags help organize and classify data in your attack surface"
        disabled={creatingAttribute === 'pending'}
        onSubmit={handlers.handleAddTag}
      />

      <TechnologyRemoveTag
        open={modalState.removeTagModal}
        onClose={modalHandlers.closeRemoveTagModal}
        technologyKeys={technology ? [technology.key] : []}
      />

      <NoteModal
        open={modalState.noteModal}
        onClose={modalHandlers.closeNoteModal}
        onSubmit={handlers.handleNote}
        resourceType="Technology"
        mode="markdown"
        selectedCount={1}
        presentation="modal"
      />

      <UniversalExportModal
        config={technologyExportConfig}
        open={modalState.exportModal}
        onClose={modalHandlers.closeExportModal}
        selectedRows={technology?.key ? [technology.key] : []}
        onClearSelection={modalHandlers.closeExportModal}
      />

      <ConstantineModal
        open={constantineModalOpen}
        onClose={() => setConstantineModalOpen(false)}
        technology={technology}
      />

      <TechnologyDrawerTabs
        technologyTabOptions={technologyTabOptions}
        currentTab={currentTab}
        onTabChange={setCurrentTab}
      />
      <div
        className={cn(
          'size-full',
          currentTab === 'evidence'
            ? 'overflow-y-hidden' // hide the overflow when the evidence tab is selected, as dropdown needs to be static
            : 'overflow-y-auto'
        )}
      >
        <Container
          className={cn(
            'h-full',
            currentTab === 'evidence' ? 'overflow-y-hidden' : 'overflow-y-auto'
          )}
          tabIndex={-1}
        >
          {currentTab === '' && (
            <TechnologyAssetsTab technologyKey={drawerKey} />
          )}
          {currentTab === 'evidence' && (
            <TechnologyEvidence
              technology={technology}
              impactedAssetsTreeStatus={impactedAssetsTreeStatus}
              impactedAssetPorts={impactedAssetPorts}
            />
          )}
          {currentTab === 'history' && (
            <TechnologyHistory
              technology={technology}
              impactedAssetPorts={impactedAssetPorts}
              impactedAssetsTreeStatus={impactedAssetsTreeStatus}
            />
          )}
          {currentTab === 'notes' && (
            <NotesTab
              resource={technology}
              resourceType="Technology"
              onOpenCommentModal={
                canWriteAssets ? modalHandlers.openNoteModal : undefined
              }
            />
          )}
          {currentTab === 'osint' && (
            <TechnologyOsint
              technology={technology}
              onResearchClick={() => setConstantineModalOpen(true)}
              isPraetorianUser={isPraetorianUser}
            />
          )}
        </Container>
      </div>
    </div>
  );
}
